import React, { useEffect, useState } from 'react';
import {
  Shield,
  Lock,
  Globe,
  FileText,
  Cookie,
  Database,
  AlertTriangle,
  ShieldCheck,
  Mail,
  Timer,
  Users,
  Activity,
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const effectiveDate = 'Nov 12, 2025';

const heroHighlights = [
  {
    icon: Shield,
    label: 'Enterprise-Grade Safeguards',
    description: 'Encryption in transit, role-based access, proactive monitoring',
  },
  {
    icon: Globe,
    label: 'Global Compliance',
    description: 'Built for GDPR, DPDP Act 2023, CCPA/CPRA readiness',
  },
  {
    icon: Lock,
    label: 'Transparency First',
    description: 'Clear consent controls, easy-to-use privacy center',
  },
];

const policySections = [
  {
    id: 'information',
    title: '1. Information We Collect',
    description:
      'We collect only what we need to power the platform and enhance your experience. Information falls into three categories:',
    icon: Database,
    items: [
      {
        label: 'Provided data',
        detail:
          'Name, email, phone number, physical and billing addresses, payment identifiers (processed securely by PCI-DSS compliant partners).',
      },
      {
        label: 'Automatic data',
        detail:
          'Device IDs, IP addresses, browser characteristics, usage logs, cookies, and analytics events to maintain performance and security.',
      },
      {
        label: 'Optional data',
        detail:
          'Marketing preferences, survey answers, beta feature feedback—only when you choose to share it.',
      },
    ],
    image:
      'https://plus.unsplash.com/premium_vector-1682309422958-663f27ed9b2f?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTl8fGRhdGF8ZW58MHx8MHx8fDA%3D',
  },
  {
    id: 'usage',
    title: '2. How We Use Information',
    description:
      'We apply strict purpose limitation. Every data use is mapped to the legitimate purpose that enables the TrackYourWheelz experience.',
    icon: Activity,
    items: [
      {
        label: 'Deliver and improve services',
        detail:
          'Account onboarding, fleet operations, feature enhancements, quality assurance, and personalized dashboards.',
      },
      {
        label: 'Security & fraud prevention',
        detail:
          'Access controls, anomaly detection, debugging, and incident response to protect accounts and infrastructure.',
      },
      {
        label: 'Compliance & communication',
        detail:
          'Audit trails, tax and accounting records, legal requests, and opt-in marketing with easy unsubscribe options.',
      },
    ],
    image:
      'https://plus.unsplash.com/premium_vector-1733721428842-7724c6c0d8e7?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8aW5mb3JtYXRpb258ZW58MHx8MHx8fDA%3D',
  },
  {
    id: 'sharing',
    title: '3. Sharing & Disclosure',
    description:
      'We do not sell personal data. When sharing is necessary, we ensure suitable safeguards and contractual controls.',
    icon: Users,
    items: [
      {
        label: 'Operational partners',
        detail:
          'Payment gateways, logistics, analytics, cloud hosting, customer support vendors—each bound by confidentiality and data protection terms.',
      },
      {
        label: 'Legal obligations',
        detail:
          'Law enforcement requests, regulatory inquiries, and tax authorities—only when required by applicable law.',
      },
      {
        label: 'Corporate transactions',
        detail:
          'Mergers, acquisitions, or asset transfers with notice and the same level of protection.',
      },
    ],
    image:
      'https://plus.unsplash.com/premium_vector-1726007325325-b7d729acc221?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c2hhcmluZyUyMGluZm9ybWF0aW9ufGVufDB8fDB8fHww',
  },
  {
    id: 'rights',
    title: '4. Your Privacy Controls & Rights',
    description:
      'Privacy is a shared journey. Depending on your jurisdiction, you may have additional statutory rights. We make exercising them simple.',
    icon: ShieldCheck,
    items: [
      {
        label: 'Access & Portability',
        detail:
          'Request a copy of your data or export it in a machine-readable format via privacy@yourbusiness.com.',
      },
      {
        label: 'Rectification & Deletion',
        detail:
          'Update inaccurate information or request deletion where legal grounds permit. We respond within legally mandated timelines.',
      },
      {
        label: 'Restriction & Objection',
        detail:
          'Suspend processing in specific contexts or opt out of marketing communications at any time.',
      },
    ],
    image:
      'https://plus.unsplash.com/premium_vector-1727544387699-6d1225fad4d8?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8Y3liZXIlMjBzZWN1cml0eXxlbnwwfHwwfHx8MA%3D%3D',
  },
];

const complianceHighlights = [
  {
    icon: Shield,
    title: 'Security',
    description:
      'Admin, technical, and physical safeguards with 256-bit TLS encryption, SOC2-aligned controls, and continuous monitoring.',
  },
  {
    icon: Cookie,
    title: 'Cookies & Tracking',
    description:
      'Essential cookies keep the platform functional. Analytics and advertising cookies require opt-in where mandated. Manage preferences via our banner.',
  },
  {
    icon: Timer,
    title: 'Data Retention',
    description:
      'Data is retained only for as long as necessary to provide services, comply with law, or resolve disputes. Retention schedules align with tax and accounting obligations.',
  },
  {
    icon: AlertTriangle,
    title: 'Children’s Privacy',
    description:
      'We do not knowingly collect data from children under the age limit defined by local regulations. Parents can contact us for deletion requests.',
  },
  {
    icon: Globe,
    title: 'International Transfers',
    description:
      'Cross-border transfers rely on approved mechanisms such as Standard Contractual Clauses and region-specific addenda.',
  },
  {
    icon: FileText,
    title: 'Policy Updates',
    description:
      'We publish changes with revised effective dates and notify you where required. Continued use signals acceptance of updates.',
  },
];

const PrivacyPolicy = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <>
      <div
        className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] text-slate-100'
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Privacy Policy" />

        <div className="absolute inset-0 pointer-events-none">
          {theme === 'dark' ? (
            <>
              <div className="absolute -top-24 -left-24 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/3 right-10 w-72 h-72 bg-indigo-500/30 rounded-full blur-[140px] animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-[160px] animate-pulse delay-500" />
            </>
          ) : (
            <>
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
            </>
          )}
        </div>

        <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${theme === 'dark' ? 'bg-slate-900 border-1 border-muted-foreground/20 text-slate-100' : 'bg-white border-1 border-muted-foreground/20 text-slate-700'}`}>
                <Shield className="h-4 w-4" />
                Privacy Policy
              </div>
              <h1 className="text-sm sm:text-3xl font-bold">How TrackYourWheelz Handles Your Data</h1>
              <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                This policy explains what data we collect, how we use it, who we share it with, and the rights available to you.
                Please review it carefully to understand how we protect your privacy.
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                Effective date: <span className="font-semibold">{effectiveDate}</span>
              </p>
            </header>

            <section className={`rounded-xl border-1 border-muted-foreground/20 ${theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'} p-6 space-y-3`}>
              <h2 className="text-sm sm:text-base font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-500" />
                Quick Overview
              </h2>
              <ul className={`list-disc ps-5 space-y-2 text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                {heroHighlights.map((item) => (
                  <li key={item.label}>
                    <span className="font-semibold">{item.label}:</span> {item.description}
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-6">
              {policySections.map((section) => (
                <Card
                  key={section.title}
                  className={`border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'}`}
                >
                  <CardHeader className="flex flex-row items-start gap-3 p-6 pb-3">
                    <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                      <section.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-sm sm:text-lg font-semibold">{section.title}</CardTitle>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>{section.description}</p>
                    </div>
                  </CardHeader>
                  <CardContent className={`p-6 pt-0 space-y-3 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                    {section.items.map((item) => (
                      <div key={item.label} className="space-y-1">
                        <p className="text-sm font-semibold">{item.label}</p>
                        <p className="text-sm leading-relaxed">{item.detail}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </section>

            <section>
              <h2 className="text-sm sm:text-base font-semibold mb-4">Compliance Highlights</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {complianceHighlights.map((item) => (
                  <Card
                    key={item.title}
                    className={`border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'}`}
                  >
                    <CardHeader className="flex items-start gap-3 p-5 pb-2">
                      <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                        <item.icon className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-sm sm:text-base font-semibold">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent className={`p-5 pt-2 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                      <p className="text-sm leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <footer className={`rounded-xl border ${theme === 'dark' ? 'border-1 border-muted-foreground/20 bg-slate-900/60' : 'border-1 border-muted-foreground/20 bg-white'} p-6 space-y-4`}>
              <h2 className="text-sm sm:text-lg font-semibold">Need help with your privacy rights?</h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                Our privacy office responds to every request within the legally required timeframe. Email us or review this policy again at any time.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => window.open('mailto:privacy@yourbusiness.com', '_blank')} className="w-full sm:w-48" variant="outline">
                  Email Privacy Office
                </Button>
                <Button variant="outline" onClick={() => document.getElementById('information')?.scrollIntoView({ behavior: 'smooth' })} className="w-full sm:w-48">
                  Review Policy Again
                </Button>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default PrivacyPolicy;